import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Check, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

export default function AdminReviews() {
  const { authHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await api.get("/admin/reviews", { headers: authHeaders() });
    setReviews(data);
  };

  const toggleApprove = async (r) => {
    await api.put(`/admin/reviews/${r.id}`, { is_approved: !r.is_approved }, { headers: authHeaders() });
    toast.success(r.is_approved ? "Unpublished" : "Approved");
    load();
  };

  const toggleFeatured = async (r) => {
    await api.put(`/admin/reviews/${r.id}`, { is_featured: !r.is_featured }, { headers: authHeaders() });
    toast.success("Updated");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <div data-testid="admin-reviews">
      <h1 className="text-3xl font-extrabold mb-6">Reviews</h1>
      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-review-${r.id}`}>
            <div className="flex justify-between items-start gap-4 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-bold text-brand-navy">{r.name}</div>
                  {r.is_approved ? (
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded">Approved</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-bold rounded">Pending</span>
                  )}
                  {r.is_featured && (
                    <span className="px-2 py-0.5 bg-pink-50 text-brand-pink text-xs font-bold rounded">Featured</span>
                  )}
                </div>
                <div className="text-sm text-slate-500">{r.location}</div>
              </div>
              <div className="flex gap-1">
                {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
            </div>
            <p className="text-slate-700 italic mb-4">"{r.comment}"</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => toggleApprove(r)} className="bg-brand-pink hover:bg-brand-pink-hover" data-testid={`approve-${r.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> {r.is_approved ? "Unpublish" : "Approve"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggleFeatured(r)} data-testid={`feature-${r.id}`}>
                {r.is_featured ? "Unfeature" : "Feature"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
